/**
 * Draft model for the employer case builder. Field names are snake_case to map
 * straight onto the POST /employer/verification-requests/submit/ payload.
 */
export const MAX_PROFILES_PER_CASE = 5;
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export interface CaseFileMeta {
  s3_key: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
}

export interface CaseEducationEntry {
  id: string;
  degree_name: string;
  school_name: string;
  education_level: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  location_country: string;
  description: string;
}

export interface CaseWorkEntry {
  id: string;
  job_title: string;
  company_name: string;
  industry: string;
  employment_type: string;
  work_mode: string;
  location: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  job_description: string;
}

export interface CaseCertEntry {
  id: string;
  qualification_name: string;
  issuing_organisation: string;
  issue_date: string;
  expiry_date: string;
}

export interface CaseAccountProfile {
  given_name: string;
  family_name: string;
  email: string;
  birth_year: string;
  country_code: string;
  phone_number: string;
  headline: string;
  location_country: string;
  location_city: string;
  bio: string;
}

/**
 * `tally` — an existing Tally candidate: the employer picks them and ticks
 * records the candidate already holds, so nothing is retyped and the case is
 * tied to their account. `manual` — someone not on Tally: the employer types
 * the records and supplies a signed consent document (the original flow).
 */
export type CaseProfileMode = 'tally' | 'manual';

/** The on-platform candidate a `tally` profile addresses. */
export interface CaseCandidateRef {
  user_id: number;
  name: string;
  headline: string;
  slug: string;
}

/** Ids of the candidate's own records the employer wants verified. */
export interface CaseSelectedEntries {
  education: number[];
  work_experience: number[];
  certificates: number[];
}

export type CaseEntryKind = keyof CaseSelectedEntries;

export interface CaseProfile {
  profile_id: string;
  mode: CaseProfileMode;
  /** `tally` mode only. */
  candidate: CaseCandidateRef | null;
  /** `tally` mode only. */
  selected_entries: CaseSelectedEntries;
  account_profile: CaseAccountProfile;
  education_entries: CaseEducationEntry[];
  work_entries: CaseWorkEntry[];
  certificate_entries: CaseCertEntry[];
  resume_file: CaseFileMeta | null;
  employee_consent_file: CaseFileMeta | null;
}

export function countSelectedEntries(selected: CaseSelectedEntries): number {
  return selected.education.length + selected.work_experience.length + selected.certificates.length;
}

export type FileKind = 'employee_resume' | 'employee_consent';

export function emptyProfile(profile_id: string): CaseProfile {
  return {
    profile_id,
    // On-platform is the default: most requests are for candidates already on
    // Tally, and picking one avoids retyping everything.
    mode: 'tally',
    candidate: null,
    selected_entries: { education: [], work_experience: [], certificates: [] },
    account_profile: {
      given_name: '',
      family_name: '',
      email: '',
      birth_year: '',
      country_code: '+1',
      phone_number: '',
      headline: '',
      location_country: '',
      location_city: '',
      bio: '',
    },
    education_entries: [],
    work_entries: [],
    certificate_entries: [],
    resume_file: null,
    employee_consent_file: null,
  };
}

/**
 * Validation, per mode:
 *  - `tally`: a candidate is picked and at least one of their records is
 *    ticked. No consent file — the candidate consents in Tally instead.
 *  - `manual`: both names, a phone, and a consent file. Phone is required
 *    because the backend's `_normalize_employee_case_account_profile` rejects
 *    the submission without it; leaving it optional here only moved the
 *    failure to submit time.
 */
export function profileIsValid(p: CaseProfile): boolean {
  if (p.mode === 'tally') {
    return !!p.candidate && countSelectedEntries(p.selected_entries) > 0;
  }
  return (
    p.account_profile.given_name.trim().length > 0 &&
    p.account_profile.family_name.trim().length > 0 &&
    p.account_profile.country_code.trim().length > 0 &&
    p.account_profile.phone_number.trim().length > 0 &&
    !!p.employee_consent_file
  );
}

/* ---- Submitted case shapes returned by the cases list/detail endpoints ---- */

export interface SubmittedSubtask {
  id: string;
  type: string;
  status: string;
  label?: string;
  reference?: string;
}

export type CandidateConsentState = 'not_required' | 'pending' | 'approved' | 'declined';

export interface SubmittedCase {
  employeeCaseId: number;
  status: string;
  processingStatus: string;
  /** True when the case addresses an on-platform candidate (issue #37). */
  isOnPlatform?: boolean;
  candidateUserId?: number | null;
  candidateConsentState?: CandidateConsentState;
  candidateConsentAt?: string | null;
  createdAt: string;
  submittedAt: string;
  updatedAt: string;
  accountProfile: Record<string, unknown> & {
    givenName?: string;
    surname?: string;
    email?: string;
    headline?: string;
  };
  educationEntries: unknown[];
  workEntries: unknown[];
  licenseEntries: unknown[];
  resumeFileMeta: { name?: string; fileUrl?: string | null } | null;
  employeeConsentFileMeta: { name?: string; fileUrl?: string | null } | null;
  subtasks: SubmittedSubtask[];
  outcomeSummary?: string;
}

export interface SubmittedCasesPage {
  count: number;
  page: number;
  page_size: number;
  next_page: number | null;
  previous_page: number | null;
  results: SubmittedCase[];
}
